import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, Label, CurrencyInput } from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { useNavigate } from "react-router-dom";
import {
  PiggyBank,
  TrendingUp,
  Check,
  Loader2,
  Info,
  Calendar,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { companyApi } from "@/services/api";
import { ResultDialog } from "@/components/ui/result-dialog";

interface ApplicationInfo {
  availableBalance: number;
  availableBalanceFormatted: string;
  appliedBalance: number;
  appliedBalanceFormatted: string;
  accumulatedYield: number;
  accumulatedYieldFormatted: string;
  cdiRate: number;
  annualRate: number;
  monthlyYieldEstimate: number;
  annualYieldEstimate: number;
}

export function CompanyAplicarPage() {
  const navigate = useNavigate();
  const [applicationInfo, setApplicationInfo] = useState<ApplicationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState("");
  const [amountCents, setAmountCents] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState<{
    success: boolean;
    title: string;
    message: string;
  } | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await companyApi.dashboard.getApplicationInfo();
      setApplicationInfo(data);
    } catch (error) {
      console.error("Erro ao carregar informações:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const availableReais = (applicationInfo?.availableBalance || 0) / 100;
  const cdiRate = applicationInfo?.cdiRate || 95;
  const annualRate = applicationInfo?.annualRate || 12.59;

  const amountReais = amountCents / 100;

  // Calcula estimativas
  const estimatedMonthlyYield = (amountReais * annualRate) / 100 / 12;
  const estimated6MonthsYield = estimatedMonthlyYield * 6;
  const estimatedAnnualYield = (amountReais * annualRate) / 100;

  const handleSetMax = () => {
    const formatted = availableReais.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setAmount(formatted);
    setAmountCents(applicationInfo?.availableBalance || 0);
  };

  const handleAmountChange = (value: string, cents: number) => {
    setAmount(value);
    setAmountCents(cents);
  };

  const handleSubmit = async () => {
    if (amountCents < 100) {
      setResultData({
        success: false,
        title: "Valor inválido",
        message: "O valor mínimo para aplicação é R$ 1,00",
      });
      setShowResult(true);
      return;
    }

    if (amountCents > (applicationInfo?.availableBalance || 0)) {
      setResultData({
        success: false,
        title: "Saldo insuficiente",
        message: "O valor informado é maior que o saldo disponível",
      });
      setShowResult(true);
      return;
    }

    setSubmitting(true);
    try {
      const result = await companyApi.dashboard.applyBrld(amountCents);
      setResultData({
        success: true,
        title: "Aplicação realizada!",
        message: `${formatCurrency(result.amount / 100)} foram aplicados com sucesso. O rendimento começa a contar a partir de ${result.yieldStartDate}.`,
      });
      setShowResult(true);
      setAmount("");
      setAmountCents(0);
      loadData();
    } catch (error: any) {
      setResultData({
        success: false,
        title: "Erro na aplicação",
        message: error.message || "Não foi possível realizar a aplicação. Tente novamente.",
      });
      setShowResult(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<PiggyBank className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Aplicar BRLD"
        description="Aplique seu saldo para começar a render"
        stats={[
          {
            value: loading ? "..." : formatCurrency(availableReais),
            label: "Saldo Disponível",
          },
          {
            value: loading ? "..." : `${annualRate.toFixed(2)}%`,
            label: "Taxa a.a.",
          },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Formulário de aplicação */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quanto deseja aplicar?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="amount">Valor em Reais</Label>
                      <Button variant="ghost" size="sm" onClick={handleSetMax}>
                        Máximo
                      </Button>
                    </div>
                    <CurrencyInput
                      id="amount"
                      currency="BRL"
                      value={amount}
                      onChange={handleAmountChange}
                      className="text-xl h-14"
                    />
                    <p className="text-xs text-muted-foreground">
                      Saldo disponível: {formatCurrency(availableReais)}
                    </p>
                  </div>

                  {amountReais > 0 && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
                      <h4 className="font-medium text-green-800 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Estimativa de Rendimento
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-green-600">1 mês</p>
                          <p className="text-sm font-semibold text-green-800">
                            +{formatCurrency(estimatedMonthlyYield)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-green-600">6 meses</p>
                          <p className="text-sm font-semibold text-green-800">
                            +{formatCurrency(estimated6MonthsYield)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-green-600">12 meses</p>
                          <p className="text-sm font-semibold text-green-800">
                            +{formatCurrency(estimatedAnnualYield)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    className="w-full h-12"
                    onClick={handleSubmit}
                    disabled={submitting || amountCents < 100 || amountCents > (applicationInfo?.availableBalance || 0)}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Aplicar {amountReais > 0 ? formatCurrency(amountReais) : ""}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Informações do produto */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Produto de Investimento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">BRLD Renda Fixa</p>
                  <p className="text-xs text-muted-foreground">{cdiRate}% do CDI</p>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rentabilidade</span>
                  <span className="font-medium">{cdiRate}% do CDI</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Taxa atual</span>
                  <span className="font-medium">{annualRate.toFixed(2)}% a.a.</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Lastro</span>
                  <span className="font-medium">100% Tesouro Selic</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Liquidez</span>
                  <span className="font-medium">D+0 (imediato)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Carência</span>
                  <span className="font-medium">Sem carência</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-sm">
                  <p className="font-medium">Regra D+1</p>
                  <p className="text-muted-foreground">
                    O rendimento começa a contar a partir do próximo dia útil após a aplicação.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Info className="w-4 h-4 text-green-600" />
                </div>
                <div className="text-sm">
                  <p className="font-medium">Resgate a qualquer momento</p>
                  <p className="text-muted-foreground">
                    Você pode resgatar sua aplicação quando quiser, sem taxas ou penalidades.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog de resultado */}
      <ResultDialog
        open={showResult}
        onClose={() => {
          setShowResult(false);
          if (resultData?.success) {
            navigate("/brld");
          }
        }}
        variant={resultData?.success ? "success" : "error"}
        title={resultData?.title || ""}
        description={resultData?.message || ""}
      />
    </div>
  );
}
